/**
 * Expression Parser
 *
 * Hand-written recursive descent parser for safe expression evaluation.
 * Supports arithmetic, comparison, logical operators, ternary, and allowed function calls.
 */
import { ALLOWED_FUNCTIONS, ALLOWED_METHODS, ALLOWED_CONTEXTS, EXPRESSION_LIMITS, } from './types';
/**
 * Parser error with position information
 */
export class ParseError extends Error {
    constructor(message, position) {
        super(`Parse error at position ${position}: ${message}`);
        this.position = position;
        this.name = 'ParseError';
    }
}
/**
 * Lexer for tokenizing expression strings
 */
class Lexer {
    constructor(input) {
        this.input = input;
        this.position = 0;
        this.tokens = [];
    }
    tokenize() {
        while (this.position < this.input.length) {
            this.skipWhitespace();
            if (this.position >= this.input.length)
                break;
            const char = this.input[this.position];
            // String literals
            if (char === '"' || char === "'") {
                this.readString(char);
                continue;
            }
            // Numbers
            if (this.isDigit(char) || (char === '.' && this.isDigit(this.peek(1)))) {
                this.readNumber();
                continue;
            }
            // Identifiers and keywords
            if (this.isIdentifierStart(char)) {
                this.readIdentifier();
                continue;
            }
            // Multi-character operators
            if (this.tryReadOperator()) {
                continue;
            }
            // Single-character punctuators
            if ('().,?:'.includes(char)) {
                this.tokens.push({
                    type: 'PUNCTUATOR',
                    value: char,
                    position: this.position,
                });
                this.position++;
                continue;
            }
            // Single-character operators
            if ('+-*/%!<>'.includes(char)) {
                this.tokens.push({
                    type: 'OPERATOR',
                    value: char,
                    position: this.position,
                });
                this.position++;
                continue;
            }
            throw new ParseError(`Unexpected character: ${char}`, this.position);
        }
        this.tokens.push({ type: 'EOF', value: '', position: this.position });
        return this.tokens;
    }
    skipWhitespace() {
        while (this.position < this.input.length && /\s/.test(this.input[this.position])) {
            this.position++;
        }
    }
    peek(offset = 0) {
        return this.input[this.position + offset] || '';
    }
    isDigit(char) {
        return /[0-9]/.test(char);
    }
    isIdentifierStart(char) {
        return /[a-zA-Z_$]/.test(char);
    }
    isIdentifierPart(char) {
        return /[a-zA-Z0-9_$]/.test(char);
    }
    readString(quote) {
        const start = this.position;
        this.position++; // skip opening quote
        let value = '';
        while (this.position < this.input.length) {
            const char = this.input[this.position];
            if (char === quote) {
                this.position++; // skip closing quote
                if (value.length > EXPRESSION_LIMITS.MAX_STRING_LENGTH) {
                    throw new ParseError(`String literal exceeds maximum length of ${EXPRESSION_LIMITS.MAX_STRING_LENGTH}`, start);
                }
                this.tokens.push({ type: 'STRING', value, position: start });
                return;
            }
            if (char === '\\' && this.position + 1 < this.input.length) {
                // Handle escape sequences
                this.position++;
                const escaped = this.input[this.position];
                switch (escaped) {
                    case 'n':
                        value += '\n';
                        break;
                    case 't':
                        value += '\t';
                        break;
                    case 'r':
                        value += '\r';
                        break;
                    case '\\':
                        value += '\\';
                        break;
                    case "'":
                        value += "'";
                        break;
                    case '"':
                        value += '"';
                        break;
                    default:
                        value += escaped;
                }
                this.position++;
            }
            else {
                value += char;
                this.position++;
            }
        }
        throw new ParseError('Unterminated string literal', start);
    }
    readNumber() {
        const start = this.position;
        let value = '';
        let hasDecimal = false;
        while (this.position < this.input.length) {
            const char = this.input[this.position];
            if (this.isDigit(char)) {
                value += char;
                this.position++;
            }
            else if (char === '.' && !hasDecimal) {
                hasDecimal = true;
                value += char;
                this.position++;
            }
            else {
                break;
            }
        }
        this.tokens.push({ type: 'NUMBER', value, position: start });
    }
    readIdentifier() {
        const start = this.position;
        let value = '';
        while (this.position < this.input.length && this.isIdentifierPart(this.input[this.position])) {
            value += this.input[this.position];
            this.position++;
        }
        this.tokens.push({ type: 'IDENTIFIER', value, position: start });
    }
    tryReadOperator() {
        // Try multi-character operators first (order matters)
        const threeChar = this.input.slice(this.position, this.position + 3);
        if (threeChar === '===' || threeChar === '!==') {
            this.tokens.push({
                type: 'OPERATOR',
                value: threeChar,
                position: this.position,
            });
            this.position += 3;
            return true;
        }
        const twoChar = this.input.slice(this.position, this.position + 2);
        if (['>=', '<=', '&&', '||'].includes(twoChar)) {
            this.tokens.push({
                type: 'OPERATOR',
                value: twoChar,
                position: this.position,
            });
            this.position += 2;
            return true;
        }
        return false;
    }
}
/**
 * Recursive descent parser
 */
export class Parser {
    constructor(input) {
        this.input = input;
        this.tokens = [];
        this.current = 0;
        this.nodeCount = 0;
        this.maxDepth = 0;
        if (input.length > EXPRESSION_LIMITS.MAX_LENGTH) {
            throw new ParseError(`Expression exceeds maximum length of ${EXPRESSION_LIMITS.MAX_LENGTH}`, 0);
        }
    }
    parse() {
        const lexer = new Lexer(this.input);
        this.tokens = lexer.tokenize();
        this.current = 0;
        this.nodeCount = 0;
        this.maxDepth = 0;
        const ast = this.parseExpression(0);
        if (!this.isAtEnd()) {
            throw new ParseError(`Unexpected token: ${this.peek().value}`, this.peek().position);
        }
        return ast;
    }
    getMetrics() {
        return { nodeCount: this.nodeCount, maxDepth: this.maxDepth };
    }
    createNode(node, depth) {
        this.nodeCount++;
        this.maxDepth = Math.max(this.maxDepth, depth);
        if (this.nodeCount > EXPRESSION_LIMITS.MAX_NODES) {
            throw new ParseError(`Expression exceeds maximum complexity of ${EXPRESSION_LIMITS.MAX_NODES} nodes`, this.peek().position);
        }
        if (depth > EXPRESSION_LIMITS.MAX_DEPTH) {
            throw new ParseError(`Expression exceeds maximum nesting depth of ${EXPRESSION_LIMITS.MAX_DEPTH}`, this.peek().position);
        }
        return node;
    }
    peek() {
        return this.tokens[this.current];
    }
    previous() {
        return this.tokens[this.current - 1];
    }
    isAtEnd() {
        return this.peek().type === 'EOF';
    }
    advance() {
        if (!this.isAtEnd())
            this.current++;
        return this.previous();
    }
    check(type, value) {
        if (this.isAtEnd())
            return false;
        const token = this.peek();
        return token.type === type && (value === undefined || token.value === value);
    }
    match(type, value) {
        if (this.check(type, value)) {
            this.advance();
            return true;
        }
        return false;
    }
    expect(type, value) {
        if (this.check(type, value)) {
            return this.advance();
        }
        throw new ParseError(`Expected ${value || type}, got ${this.peek().value}`, this.peek().position);
    }
    // Expression parsing using precedence climbing
    parseExpression(depth) {
        return this.parseTernary(depth);
    }
    parseTernary(depth) {
        let expr = this.parseLogicalOr(depth);
        if (this.match('PUNCTUATOR', '?')) {
            const consequent = this.parseExpression(depth + 1);
            this.expect('PUNCTUATOR', ':');
            const alternate = this.parseExpression(depth + 1);
            expr = this.createNode({
                type: 'ConditionalExpression',
                test: expr,
                consequent,
                alternate,
            }, depth);
        }
        return expr;
    }
    parseLogicalOr(depth) {
        let left = this.parseLogicalAnd(depth);
        while (this.match('OPERATOR', '||')) {
            const right = this.parseLogicalAnd(depth + 1);
            left = this.createNode({
                type: 'BinaryExpression',
                operator: '||',
                left,
                right,
            }, depth);
        }
        return left;
    }
    parseLogicalAnd(depth) {
        let left = this.parseEquality(depth);
        while (this.match('OPERATOR', '&&')) {
            const right = this.parseEquality(depth + 1);
            left = this.createNode({
                type: 'BinaryExpression',
                operator: '&&',
                left,
                right,
            }, depth);
        }
        return left;
    }
    parseEquality(depth) {
        let left = this.parseComparison(depth);
        while (this.check('OPERATOR')) {
            const op = this.peek().value;
            if (op === '===' || op === '!==') {
                this.advance();
                const right = this.parseComparison(depth + 1);
                left = this.createNode({
                    type: 'BinaryExpression',
                    operator: op,
                    left,
                    right,
                }, depth);
            }
            else {
                break;
            }
        }
        return left;
    }
    parseComparison(depth) {
        let left = this.parseAdditive(depth);
        while (this.check('OPERATOR')) {
            const op = this.peek().value;
            if (['>', '<', '>=', '<='].includes(op)) {
                this.advance();
                const right = this.parseAdditive(depth + 1);
                left = this.createNode({
                    type: 'BinaryExpression',
                    operator: op,
                    left,
                    right,
                }, depth);
            }
            else {
                break;
            }
        }
        return left;
    }
    parseAdditive(depth) {
        let left = this.parseMultiplicative(depth);
        while (this.check('OPERATOR')) {
            const op = this.peek().value;
            if (op === '+' || op === '-') {
                this.advance();
                const right = this.parseMultiplicative(depth + 1);
                left = this.createNode({
                    type: 'BinaryExpression',
                    operator: op,
                    left,
                    right,
                }, depth);
            }
            else {
                break;
            }
        }
        return left;
    }
    parseMultiplicative(depth) {
        let left = this.parseUnary(depth);
        while (this.check('OPERATOR')) {
            const op = this.peek().value;
            if (op === '*' || op === '/' || op === '%') {
                this.advance();
                const right = this.parseUnary(depth + 1);
                left = this.createNode({
                    type: 'BinaryExpression',
                    operator: op,
                    left,
                    right,
                }, depth);
            }
            else {
                break;
            }
        }
        return left;
    }
    parseUnary(depth) {
        if (this.check('OPERATOR')) {
            const op = this.peek().value;
            if (op === '!' || op === '-' || op === '+') {
                this.advance();
                const argument = this.parseUnary(depth + 1);
                return this.createNode({
                    type: 'UnaryExpression',
                    operator: op,
                    argument,
                }, depth);
            }
        }
        return this.parseCallOrMember(depth);
    }
    parseCallOrMember(depth) {
        let expr = this.parsePrimary(depth);
        while (true) {
            if (this.match('PUNCTUATOR', '.')) {
                // Member access
                const propToken = this.expect('IDENTIFIER');
                const property = propToken.value;
                // Check if this is a function/method call
                if (this.match('PUNCTUATOR', '(')) {
                    // Check if it's a global function like Math.round(x)
                    if (expr.type === 'Identifier') {
                        const callee = `${expr.name}.${property}`;
                        if (ALLOWED_FUNCTIONS.includes(callee)) {
                            const args = this.parseArguments(depth + 1);
                            expr = this.createNode({
                                type: 'CallExpression',
                                callee,
                                arguments: args,
                            }, depth);
                            continue;
                        }
                    }
                    // It's a method call on a value (e.g., str.includes('.'))
                    if (!ALLOWED_METHODS.includes(property)) {
                        throw new ParseError(`Method "${property}" is not allowed`, this.previous().position);
                    }
                    const args = this.parseArguments(depth + 1);
                    expr = this.createNode({
                        type: 'MethodCallExpression',
                        object: expr,
                        method: property,
                        arguments: args,
                    }, depth);
                }
                else {
                    // It's a property access
                    expr = this.createNode({
                        type: 'MemberExpression',
                        object: expr,
                        property,
                    }, depth);
                }
            }
            else if (this.match('PUNCTUATOR', '(')) {
                // Direct function call like Number(x)
                if (expr.type !== 'Identifier') {
                    throw new ParseError('Invalid function call', this.previous().position);
                }
                const callee = expr.name;
                if (!ALLOWED_FUNCTIONS.includes(callee)) {
                    throw new ParseError(`Function "${callee}" is not allowed`, this.previous().position);
                }
                const args = this.parseArguments(depth + 1);
                expr = this.createNode({
                    type: 'CallExpression',
                    callee,
                    arguments: args,
                }, depth);
            }
            else {
                break;
            }
        }
        return expr;
    }
    parseArguments(depth) {
        const args = [];
        if (!this.check('PUNCTUATOR', ')')) {
            do {
                args.push(this.parseExpression(depth + 1));
            } while (this.match('PUNCTUATOR', ','));
        }
        this.expect('PUNCTUATOR', ')');
        return args;
    }
    parsePrimary(depth) {
        // Boolean literals
        if (this.check('IDENTIFIER', 'true')) {
            this.advance();
            return this.createNode({ type: 'Literal', value: true }, depth);
        }
        if (this.check('IDENTIFIER', 'false')) {
            this.advance();
            return this.createNode({ type: 'Literal', value: false }, depth);
        }
        // Null literal
        if (this.check('IDENTIFIER', 'null')) {
            this.advance();
            return this.createNode({ type: 'Literal', value: null }, depth);
        }
        // Number literal
        if (this.check('NUMBER')) {
            const token = this.advance();
            const value = parseFloat(token.value);
            return this.createNode({ type: 'Literal', value }, depth);
        }
        // String literal
        if (this.check('STRING')) {
            const token = this.advance();
            return this.createNode({ type: 'Literal', value: token.value }, depth);
        }
        // Parenthesized expression
        if (this.match('PUNCTUATOR', '(')) {
            const expr = this.parseExpression(depth + 1);
            this.expect('PUNCTUATOR', ')');
            return expr;
        }
        // Identifier (context variable)
        if (this.check('IDENTIFIER')) {
            const token = this.advance();
            const name = token.value;
            // Validate root context
            if (!ALLOWED_CONTEXTS.includes(name) &&
                name !== 'Math' &&
                name !== 'JSON' &&
                name !== 'Array' &&
                !ALLOWED_FUNCTIONS.includes(name)) {
                throw new ParseError(`Identifier "${name}" is not allowed. Use input, output, or payload`, token.position);
            }
            return this.createNode({ type: 'Identifier', name }, depth);
        }
        throw new ParseError(`Unexpected token: ${this.peek().value}`, this.peek().position);
    }
}
/**
 * Parse an expression string into an AST
 */
export function parseExpression(input) {
    const parser = new Parser(input);
    const ast = parser.parse();
    const metrics = parser.getMetrics();
    return { ast, ...metrics };
}
//# sourceMappingURL=parser.js.map